import React, { useState } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import { RISK_LEVELS, normalizeRiskLevel } from '../../utils/riskLevel';

// ── Constants ──────────────────────────────────────────────────────────────────

const categories = [
  'cybersecurity', 'data_protection', 'risk_management', 'governance',
  'incident_response', 'business_continuity', 'disaster_recovery',
  'supply_chain_cybersecurity', 'third_party_risk',
  'ai_security', 'machine_learning_security', 'cloud_security',
  'iot_security', 'devsecops', 'ci_cd_security',
  'financial_security', 'healthcare_security', 'critical_infrastructure',
  'government_security', 'defense_security',
  'privacy_compliance', 'regulatory_compliance', 'audit_compliance',
  'network_security', 'application_security', 'endpoint_security',
  'identity_access_management', 'cryptography', 'threat_intelligence'
];

const frameworkTypes = [
  'regulation', 'standard', 'framework', 'guideline',
  'best_practice', 'law', 'directive'
];



const countries = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia',
  'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
  'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
  'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde',
  'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
  'East Timor', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
  'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany',
  'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti',
  'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel',
  'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan',
  'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania',
  'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands',
  'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro',
  'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand',
  'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan',
  'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
  'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
  'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles',
  'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa',
  'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland',
  'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Togo', 'Tonga', 'Trinidad and Tobago',
  'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates',
  'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela',
  'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
];

const jurisdictions = [
  'Global',
  'European Union',
  ...countries
];

// ── Helpers ────────────────────────────────────────────────────────────────────

const getTypeDisplayName = (type) => {
  const typeMap = {
    'regulation': 'Regulation',
    'standard': 'Standard',
    'framework': 'Framework',
    'guideline': 'Guideline',
    'best_practice': 'Best Practice',
    'law': 'Law',
    'directive': 'Directive'
  };
  return typeMap[type] || type;
};

const getCategoryDisplayName = (category) => {
  const categoryMap = {
    'cybersecurity': 'Cybersecurity',
    'data_protection': 'Data Protection',
    'risk_management': 'Risk Management',
    'governance': 'Governance',
    'incident_response': 'Incident Response',
    'business_continuity': 'Business Continuity',
    'disaster_recovery': 'Disaster Recovery',
    'supply_chain_cybersecurity': 'Supply Chain Cybersecurity',
    'third_party_risk': 'Third Party Risk',
    'ai_security': 'AI Security',
    'machine_learning_security': 'Machine Learning Security',
    'cloud_security': 'Cloud Security',
    'iot_security': 'IoT Security',
    'devsecops': 'DevSecOps',
    'ci_cd_security': 'CI/CD Security',
    'financial_security': 'Financial Security',
    'healthcare_security': 'Healthcare Security',
    'critical_infrastructure': 'Critical Infrastructure',
    'government_security': 'Government Security',
    'defense_security': 'Defense Security',
    'privacy_compliance': 'Privacy Compliance',
    'regulatory_compliance': 'Regulatory Compliance',
    'audit_compliance': 'Audit Compliance',
    'network_security': 'Network Security',
    'application_security': 'Application Security',
    'endpoint_security': 'Endpoint Security',
    'identity_access_management': 'Identity & Access Management',
    'cryptography': 'Cryptography',
    'threat_intelligence': 'Threat Intelligence'
  };
  return categoryMap[category] || category;
};

const getCountryFlag = (jurisdiction) => {
  if (!jurisdiction || jurisdiction.toLowerCase().includes('global') || jurisdiction.toLowerCase().includes('international')) {
    return '\u{1F310}';
  }

  const jurisdictionToCode = {
    'united states': 'US', 'usa': 'US', 'us': 'US',
    'european union': 'EU', 'eu': 'EU',
    'united kingdom': 'GB', 'uk': 'GB',
    'canada': 'CA', 'australia': 'AU', 'germany': 'DE', 'france': 'FR',
    'italy': 'IT', 'spain': 'ES', 'netherlands': 'NL', 'japan': 'JP',
    'china': 'CN', 'india': 'IN', 'brazil': 'BR', 'mexico': 'MX',
    'singapore': 'SG', 'switzerland': 'CH', 'sweden': 'SE', 'norway': 'NO',
    'denmark': 'DK', 'finland': 'FI', 'south korea': 'KR', 'new zealand': 'NZ',
    'south africa': 'ZA', 'israel': 'IL', 'russia': 'RU', 'turkey': 'TR',
    'thailand': 'TH', 'malaysia': 'MY', 'indonesia': 'ID', 'philippines': 'PH',
    'vietnam': 'VN', 'argentina': 'AR', 'chile': 'CL', 'colombia': 'CO',
    'peru': 'PE', 'venezuela': 'VE', 'uruguay': 'UY',
    'dominican republic': 'DO', 'rep. dominicana': 'DO', 'rep\u00FAblica dominicana': 'DO',
    'costa rica': 'CR', 'panama': 'PA', 'guatemala': 'GT', 'honduras': 'HN',
    'el salvador': 'SV', 'nicaragua': 'NI', 'jamaica': 'JM',
    'trinidad and tobago': 'TT', 'barbados': 'BB', 'bahamas': 'BS',
    'cuba': 'CU', 'haiti': 'HT', 'puerto rico': 'PR'
  };

  const code = jurisdictionToCode[jurisdiction.toLowerCase()];

  if (!code) {
    const match = jurisdiction.match(/\b([A-Z]{2})\b/);
    if (match) {
      const extractedCode = match[1];
      const codePoints = [...extractedCode].map(char => 127397 + char.charCodeAt());
      return String.fromCodePoint(...codePoints);
    }
    return '\u{1F3DB}\uFE0F';
  }

  if (code === 'EU') {
    return '\u{1F1EA}\u{1F1FA}';
  }

  const codePoints = [...code].map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

// ── Initial state factories ────────────────────────────────────────────────────

const emptyFramework = () => ({
  name: '',
  short_name: '',
  type: '',
  category: '',
  jurisdiction: '',
  description: '',
  version: '',
  effective_date: '',
  website_url: '',
  risk_level: 'medium'
});

const emptyControl = () => ({ ref: '', domain: '', subdomain: '', description: '' });

// ── Component ──────────────────────────────────────────────────────────────────

function ManagementTab({ frameworks, onSuccess, onError, onWarning, onRefreshFrameworks }) {
  // Management view state
  const [managementView, setManagementView] = useState('frameworks');
  const [selectedFrameworkForManagement, setSelectedFrameworkForManagement] = useState(null);

  // Framework form state
  const [newFramework, setNewFramework] = useState(emptyFramework());
  const [isAddingFramework, setIsAddingFramework] = useState(false);
  const [editingFramework, setEditingFramework] = useState(null);

  // Controls state
  const [frameworkControls, setFrameworkControls] = useState([]);
  const [isLoadingControls, setIsLoadingControls] = useState(false);
  const [newControl, setNewControl] = useState(emptyControl());
  const [isAddingControl, setIsAddingControl] = useState(false);
  const [editingControl, setEditingControl] = useState(null);

  // Bulk import state
  const [bulkControlsText, setBulkControlsText] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploadingControls, setIsUploadingControls] = useState(false);

  // ── API helpers ────────────────────────────────────────────────────────────

  const authHeaders = () => ({
    headers: {
      'access_token': localStorage.getItem('token'),
      'Content-Type': 'application/json'
    }
  });

  const authHeadersOnly = () => ({
    headers: { 'access_token': localStorage.getItem('token') }
  });

  // ── Framework handlers ─────────────────────────────────────────────────────

  const handleAddFrameworks = async (frameworksToAdd) => {
    setIsAddingFramework(true);
    try {
      const payload = {
        frameworks: frameworksToAdd,
        overwrite_existing: false
      };

      const response = await axios.post(`${Config.APIBaseURL}/grc/frameworks/add`, payload, authHeaders());

      // Refresh frameworks list in parent
      await onRefreshFrameworks();

      onSuccess(`Successfully added ${response.data.summary.successfully_added} frameworks!`);
    } catch (error) {
      console.error('Error adding frameworks:', error);
      onError('Error adding frameworks. Please try again.');
    } finally {
      setIsAddingFramework(false);
    }
  };

  const handleAddCustomFramework = async () => {
    if (!newFramework.name || !newFramework.short_name || !newFramework.type || !newFramework.category) {
      onWarning('Please fill in all required fields');
      return;
    }

    await handleAddFrameworks([newFramework]);
    setNewFramework(emptyFramework());
  };

  const handleEditFramework = (framework) => {
    setEditingFramework({ ...framework });
  };

  const handleUpdateFramework = async () => {
    if (!editingFramework) return;

    try {
      setIsAddingFramework(true);
      await axios.put(
        `${Config.APIBaseURL}/grc/frameworks/${editingFramework.id}`,
        editingFramework,
        authHeaders()
      );

      await onRefreshFrameworks();
      setEditingFramework(null);
      onSuccess('Framework updated successfully!');
    } catch (error) {
      console.error('Error updating framework:', error);
      onError('Error updating framework. Please try again.');
    } finally {
      setIsAddingFramework(false);
    }
  };

  const handleDeleteFramework = async (frameworkId) => {
    if (!window.confirm('Are you sure you want to delete this framework? This action cannot be undone.')) {
      return;
    }

    try {
      setIsAddingFramework(true);
      await axios.delete(`${Config.APIBaseURL}/grc/frameworks/${frameworkId}`, authHeadersOnly());

      await onRefreshFrameworks();
      onSuccess('Framework deleted successfully!');
    } catch (error) {
      console.error('Error deleting framework:', error);
      onError('Error deleting framework. Please try again.');
    } finally {
      setIsAddingFramework(false);
    }
  };

  const handleCloneFramework = async (framework) => {
    const clonedFramework = {
      ...framework,
      name: `${framework.name} (Copy)`,
      short_name: `${framework.short_name}_COPY`,
      id: undefined
    };

    await handleAddFrameworks([clonedFramework]);
  };

  // ── Control handlers ───────────────────────────────────────────────────────

  const fetchFrameworkControls = async (frameworkCatId) => {
    setIsLoadingControls(true);
    try {
      if (!frameworkCatId) {
        console.warn('Framework does not have a framework_cat_id - no controls available');
        setFrameworkControls([]);
        return;
      }

      const response = await axios.get(
        `${Config.APIBaseURL}/controls/${frameworkCatId}`,
        authHeadersOnly()
      );
      setFrameworkControls(response.data.controls_es || []);
    } catch (error) {
      console.error('Error fetching controls:', error);
      if (error.response?.status === 404) {
        console.warn('No controls found for framework_cat_id:', frameworkCatId);
      }
      setFrameworkControls([]);
    } finally {
      setIsLoadingControls(false);
    }
  };

  const handleAddControl = async () => {
    if (!newControl.ref || !newControl.description || !selectedFrameworkForManagement) {
      onWarning('Please fill in all required fields (Reference and Description)');
      return;
    }

    try {
      setIsAddingControl(true);
      const payload = {
        framework_id: selectedFrameworkForManagement.id,
        controls: [newControl]
      };

      await axios.post(`${Config.APIBaseURL}/controls`, payload, authHeaders());

      await fetchFrameworkControls(selectedFrameworkForManagement.framework_cat_id);
      setNewControl(emptyControl());
      onSuccess('Control added successfully!');
    } catch (error) {
      console.error('Error adding control:', error);
      onError('Error adding control. Please try again.');
    } finally {
      setIsAddingControl(false);
    }
  };

  const handleUpdateControl = async (control) => {
    try {
      setIsAddingControl(true);
      await axios.put(`${Config.APIBaseURL}/controls/${control.id}`, control, authHeaders());

      await fetchFrameworkControls(selectedFrameworkForManagement.framework_cat_id);
      setEditingControl(null);
      onSuccess('Control updated successfully!');
    } catch (error) {
      console.error('Error updating control:', error);
      onError('Error updating control. Please try again.');
    } finally {
      setIsAddingControl(false);
    }
  };

  const handleDeleteControl = async (controlId) => {
    if (!window.confirm('Are you sure you want to delete this control?')) {
      return;
    }

    try {
      setIsAddingControl(true);
      await axios.delete(`${Config.APIBaseURL}/controls/${controlId}`, authHeadersOnly());

      await fetchFrameworkControls(selectedFrameworkForManagement.framework_cat_id);
      onSuccess('Control deleted successfully!');
    } catch (error) {
      console.error('Error deleting control:', error);
      onError('Error deleting control. Please try again.');
    } finally {
      setIsAddingControl(false);
    }
  };

  const handleBulkAddControls = async () => {
    if (!bulkControlsText.trim() && !uploadFile) {
      onWarning('Please enter controls manually or select a file to upload');
      return;
    }

    try {
      setIsUploadingControls(true);

      if (uploadFile) {
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('framework_id', selectedFrameworkForManagement.id);

        const response = await axios.post(`${Config.APIBaseURL}/controls/upload`, formData, {
          headers: {
            'access_token': localStorage.getItem('token'),
            'Content-Type': 'multipart/form-data'
          }
        });

        onSuccess(`Successfully uploaded ${response.data.count || 0} controls!`);
      } else {
        const lines = bulkControlsText.trim().split('\n');
        const controls = lines.map(line => {
          const parts = line.split(',').map(part => part.trim());
          if (parts.length >= 2) {
            return {
              ref: parts[0] || '',
              domain: parts[1] || '',
              subdomain: parts[2] || '',
              description: parts[3] || parts[1]
            };
          }
          return null;
        }).filter(Boolean);

        if (controls.length === 0) {
          onWarning('No valid controls found. Please use format: ref,domain,subdomain,description');
          return;
        }

        const payload = {
          framework_id: selectedFrameworkForManagement.id,
          controls: controls
        };

        await axios.post(`${Config.APIBaseURL}/controls/bulk`, payload, authHeaders());

        onSuccess(`Successfully added ${controls.length} controls!`);
      }

      await fetchFrameworkControls(selectedFrameworkForManagement.framework_cat_id);
      setBulkControlsText('');
      setUploadFile(null);
    } catch (error) {
      console.error('Error adding bulk controls:', error);
      onError('Error adding controls. Please try again.');
    } finally {
      setIsUploadingControls(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="space-y-6">
        {/* Management Navigation */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
          <div className="px-6 py-4">
            <div className="flex items-center space-x-6">
              <button
                onClick={() => setManagementView('frameworks')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  managementView === 'frameworks'
                    ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
                }`}
              >
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Framework Management
                </div>
              </button>
              <button
                onClick={() => setManagementView('controls')}
                disabled={!selectedFrameworkForManagement}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  managementView === 'controls'
                    ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                    : selectedFrameworkForManagement
                    ? 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
                    : 'text-slate-400 dark:text-slate-600 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 01-2 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  Controls Management
                  {selectedFrameworkForManagement && (
                    <span className="ml-2 text-xs bg-indigo-500 text-white px-2 py-1 rounded-full">
                      {selectedFrameworkForManagement.short_name}
                    </span>
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Framework Management View */}
        {managementView === 'frameworks' && (
          <div className="space-y-6">
            {/* Existing Frameworks */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                  Existing Frameworks
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  Manage compliance frameworks - edit, clone, or delete
                </p>
              </div>

              <div className="p-6">
                {frameworks.length > 0 ? (
                  <div className="space-y-4">
                    {frameworks.map((framework) => (
                      <div
                        key={framework.id}
                        className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        <div className="flex items-center space-x-4">
                          <div>
                            <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                              {framework.name}
                            </h3>
                            <div className="flex items-center space-x-4 mt-1">
                              <span className="text-sm text-slate-600 dark:text-slate-400">
                                {framework.short_name}
                              </span>
                              <span className="text-sm text-slate-500 dark:text-slate-500">
                                {getTypeDisplayName(framework.type)}
                              </span>
                              <div className="flex items-center bg-indigo-100 dark:bg-indigo-700/50 px-2 py-1 rounded-full">
                                <span className="mr-1 text-base">{getCountryFlag(framework.jurisdiction)}</span>
                                <span className="text-indigo-800 dark:text-indigo-100 font-medium text-xs">
                                  {framework.jurisdiction}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              if (!framework.framework_cat_id) {
                                onWarning(`Framework "${framework.name}" does not have associated controls to manage.`);
                                return;
                              }
                              setSelectedFrameworkForManagement(framework);
                              setManagementView('controls');
                              fetchFrameworkControls(framework.framework_cat_id);
                            }}
                            className={`p-2 rounded-lg transition-colors ${
                              framework.framework_cat_id
                                ? 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                                : 'text-slate-400 dark:text-slate-600 cursor-not-allowed'
                            }`}
                            title={framework.framework_cat_id ? "Manage Controls" : "No controls available for this framework"}
                            disabled={!framework.framework_cat_id}
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 01-2 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleEditFramework(framework)}
                            className="p-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                            title="Edit Framework"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleCloneFramework(framework)}
                            className="p-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                            title="Clone Framework"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteFramework(framework.id)}
                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title="Delete Framework"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">No frameworks found</h3>
                    <p className="text-slate-500 dark:text-slate-400">Create your first framework below</p>
                  </div>
                )}
              </div>
            </div>

            {/* Add Custom Framework */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                  Add Custom Framework
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  Create a new compliance framework for your organization
                </p>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Framework Name *
                    </label>
                    <input
                      type="text"
                      value={newFramework.name}
                      onChange={(e) => setNewFramework({...newFramework, name: e.target.value})}
                      placeholder="e.g., General Data Protection Regulation"
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Short Name/Acronym *
                    </label>
                    <input
                      type="text"
                      value={newFramework.short_name}
                      onChange={(e) => setNewFramework({...newFramework, short_name: e.target.value})}
                      placeholder="e.g., GDPR"
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Type *
                    </label>
                    <select
                      value={newFramework.type}
                      onChange={(e) => setNewFramework({...newFramework, type: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Select type</option>
                      {frameworkTypes.map(type => (
                        <option key={type} value={type}>{getTypeDisplayName(type)}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Category *
                    </label>
                    <select
                      value={newFramework.category}
                      onChange={(e) => setNewFramework({...newFramework, category: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Select category</option>
                      {categories.map(category => (
                        <option key={category} value={category}>{getCategoryDisplayName(category)}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Jurisdiction
                    </label>
                    <select
                      value={newFramework.jurisdiction}
                      onChange={(e) => setNewFramework({...newFramework, jurisdiction: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Select jurisdiction</option>
                      {jurisdictions.map(jurisdiction => (
                        <option key={jurisdiction} value={jurisdiction}>{jurisdiction}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Risk Level
                    </label>
                    <select
                      value={normalizeRiskLevel(newFramework.risk_level) || 'medium'}
                      onChange={(e) => setNewFramework({...newFramework, risk_level: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {RISK_LEVELS.map(level => (
                        <option key={level.value} value={level.value}>{level.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Version
                    </label>
                    <input
                      type="text"
                      value={newFramework.version}
                      onChange={(e) => setNewFramework({...newFramework, version: e.target.value})}
                      placeholder="e.g., 1.0, 2023.1"
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Effective Date
                    </label>
                    <input
                      type="date"
                      value={newFramework.effective_date}
                      onChange={(e) => setNewFramework({...newFramework, effective_date: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Website URL
                    </label>
                    <input
                      type="url"
                      value={newFramework.website_url}
                      onChange={(e) => setNewFramework({...newFramework, website_url: e.target.value})}
                      placeholder="https://example.com/framework"
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newFramework.description}
                    onChange={(e) => setNewFramework({...newFramework, description: e.target.value})}
                    placeholder="Brief description of the framework..."
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="mt-6 px-6 py-4 bg-slate-50 dark:bg-slate-700/50 border-t border-slate-200 dark:border-slate-600 flex items-center justify-between">
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  * Required fields
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setNewFramework(emptyFramework())}
                    className="px-4 py-2 text-sm bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                  >
                    Clear Form
                  </button>
                  <button
                    onClick={handleAddCustomFramework}
                    disabled={isAddingFramework || !newFramework.name || !newFramework.short_name || !newFramework.type || !newFramework.category}
                    className="px-6 py-2 text-sm bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAddingFramework ? (
                      <div className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Adding...
                      </div>
                    ) : (
                      'Add Framework'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Controls Management View */}
        {managementView === 'controls' && selectedFrameworkForManagement && (
          <div className="space-y-6">
            {/* Framework Info Header */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                      Controls for {selectedFrameworkForManagement.name}
                    </h2>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                      Manage framework controls - add, edit, or bulk import
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setManagementView('frameworks');
                      setSelectedFrameworkForManagement(null);
                      setFrameworkControls([]);
                    }}
                    className="px-4 py-2 text-sm bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                  >
                    &larr; Back to Frameworks
                  </button>
                </div>
              </div>
            </div>

            {/* Add Single Control */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                  Add New Control
                </h3>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Reference *
                    </label>
                    <input
                      type="text"
                      value={newControl.ref}
                      onChange={(e) => setNewControl({...newControl, ref: e.target.value})}
                      placeholder="e.g., AC-1, PR.ID-1"
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Domain
                    </label>
                    <input
                      type="text"
                      value={newControl.domain}
                      onChange={(e) => setNewControl({...newControl, domain: e.target.value})}
                      placeholder="e.g., Access Control"
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      Subdomain
                    </label>
                    <input
                      type="text"
                      value={newControl.subdomain}
                      onChange={(e) => setNewControl({...newControl, subdomain: e.target.value})}
                      placeholder="e.g., Policy and Procedures"
                      className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={handleAddControl}
                      disabled={isAddingControl}
                      className="w-full px-4 py-2 text-sm bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isAddingControl ? 'Adding...' : 'Add Control'}
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Description *
                  </label>
                  <textarea
                    value={newControl.description}
                    onChange={(e) => setNewControl({...newControl, description: e.target.value})}
                    placeholder="Describe the control requirements and implementation guidance..."
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Bulk Import Controls */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                  Bulk Import Controls
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  Upload a CSV/XLS file or enter controls manually
                </p>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* File Upload */}
                  <div>
                    <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-3">File Upload</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Select CSV or XLS file
                        </label>
                        <input
                          type="file"
                          accept=".csv,.xlsx,.xls"
                          onChange={(e) => setUploadFile(e.target.files[0])}
                          className="w-full text-sm text-slate-500 dark:text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 dark:file:bg-indigo-900/30 dark:file:text-indigo-300"
                        />
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        <p className="font-medium mb-1">Expected format:</p>
                        <p>ref, domain, subdomain, description</p>
                        <p className="mt-1">Example: AC-1, Access Control, Policy, Establish access control policy</p>
                      </div>
                    </div>
                  </div>

                  {/* Manual Entry */}
                  <div>
                    <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-3">Manual Entry</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Enter controls (one per line)
                        </label>
                        <textarea
                          value={bulkControlsText}
                          onChange={(e) => setBulkControlsText(e.target.value)}
                          placeholder={"AC-1, Access Control, Policy, Establish access control policy\nAC-2, Access Control, Account Management, Manage user accounts\nAU-1, Audit and Accountability, Policy, Establish audit policy"}
                          rows={6}
                          className="w-full text-sm px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleBulkAddControls}
                    disabled={isUploadingControls}
                    className="px-6 py-2 text-sm bg-green-600 text-white font-medium rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploadingControls ? (
                      <div className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Importing...
                      </div>
                    ) : (
                      'Import Controls'
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Existing Controls */}
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                  Existing Controls ({frameworkControls.length})
                </h3>
              </div>

              <div className="p-6">
                {isLoadingControls ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="flex items-center">
                      <svg className="animate-spin w-5 h-5 text-indigo-600 mr-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading controls...
                    </div>
                  </div>
                ) : frameworkControls.length > 0 ? (
                  <div className="space-y-3">
                    {frameworkControls.map((control, index) => (
                      <div
                        key={control.id || index}
                        className="flex items-center justify-between p-3 border border-slate-200 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        {editingControl?.id === control.id ? (
                          <div className="flex-1 space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <input
                                type="text"
                                value={editingControl.ref || ''}
                                onChange={(e) => setEditingControl({...editingControl, ref: e.target.value})}
                                className="px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                                placeholder="Reference"
                              />
                              <input
                                type="text"
                                value={editingControl.domain || ''}
                                onChange={(e) => setEditingControl({...editingControl, domain: e.target.value})}
                                className="px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                                placeholder="Domain"
                              />
                              <input
                                type="text"
                                value={editingControl.subdomain || ''}
                                onChange={(e) => setEditingControl({...editingControl, subdomain: e.target.value})}
                                className="px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                                placeholder="Subdomain"
                              />
                            </div>
                            <textarea
                              value={editingControl.description || ''}
                              onChange={(e) => setEditingControl({...editingControl, description: e.target.value})}
                              className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                              placeholder="Description"
                              rows={3}
                            />
                          </div>
                        ) : (
                          <div className="flex-1">
                            <div className="flex items-start space-x-4">
                              <span className="font-mono text-sm font-medium text-indigo-600 dark:text-indigo-400 min-w-0">
                                {control.ref}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 text-sm text-slate-600 dark:text-slate-400 mb-1">
                                  {control.domain && <span>{control.domain}</span>}
                                  {control.domain && control.subdomain && <span>&bull;</span>}
                                  {control.subdomain && <span>{control.subdomain}</span>}
                                </div>
                                <p className="text-sm text-slate-800 dark:text-slate-200 line-clamp-2">
                                  {control.description}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="flex items-center space-x-2 ml-4">
                          {editingControl?.id === control.id ? (
                            <>
                              <button
                                onClick={() => handleUpdateControl(editingControl)}
                                disabled={isAddingControl}
                                className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded transition-colors disabled:opacity-50"
                                title="Save"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                              <button
                                onClick={() => setEditingControl(null)}
                                className="p-1.5 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-600 rounded transition-colors"
                                title="Cancel"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => setEditingControl({...control})}
                                className="p-1.5 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-600 rounded transition-colors"
                                title="Edit"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteControl(control.id)}
                                className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                                title="Delete"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <svg className="w-12 h-12 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 01-2 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">No controls found</h3>
                    <p className="text-slate-500 dark:text-slate-400">Add controls using the forms above</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Framework Edit Modal */}
      {editingFramework && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                    Edit Framework
                  </h2>
                  <p className="text-slate-600 dark:text-slate-400">
                    Modify framework details and metadata
                  </p>
                </div>
                <button
                  onClick={() => setEditingFramework(null)}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Framework Name *
                  </label>
                  <input
                    type="text"
                    value={editingFramework.name}
                    onChange={(e) => setEditingFramework({...editingFramework, name: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Short Name/Acronym *
                  </label>
                  <input
                    type="text"
                    value={editingFramework.short_name}
                    onChange={(e) => setEditingFramework({...editingFramework, short_name: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Type *
                  </label>
                  <select
                    value={editingFramework.type}
                    onChange={(e) => setEditingFramework({...editingFramework, type: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select type</option>
                    {frameworkTypes.map(type => (
                      <option key={type} value={type}>{getTypeDisplayName(type)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Category *
                  </label>
                  <select
                    value={editingFramework.category}
                    onChange={(e) => setEditingFramework({...editingFramework, category: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{getCategoryDisplayName(category)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Jurisdiction
                  </label>
                  <select
                    value={editingFramework.jurisdiction}
                    onChange={(e) => setEditingFramework({...editingFramework, jurisdiction: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Select jurisdiction</option>
                    {jurisdictions.map(jurisdiction => (
                      <option key={jurisdiction} value={jurisdiction}>{jurisdiction}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Risk Level
                  </label>
                  <select
                    value={normalizeRiskLevel(editingFramework.risk_level) || 'medium'}
                    onChange={(e) => setEditingFramework({...editingFramework, risk_level: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {RISK_LEVELS.map(level => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Version
                  </label>
                  <input
                    type="text"
                    value={editingFramework.version || ''}
                    onChange={(e) => setEditingFramework({...editingFramework, version: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Effective Date
                  </label>
                  <input
                    type="date"
                    value={editingFramework.effective_date || ''}
                    onChange={(e) => setEditingFramework({...editingFramework, effective_date: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Website URL
                  </label>
                  <input
                    type="url"
                    value={editingFramework.website_url || ''}
                    onChange={(e) => setEditingFramework({...editingFramework, website_url: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Description
                </label>
                <textarea
                  value={editingFramework.description || ''}
                  onChange={(e) => setEditingFramework({...editingFramework, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={() => setEditingFramework(null)}
                  className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateFramework}
                  disabled={isAddingFramework}
                  className="px-6 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAddingFramework ? 'Updating...' : 'Update Framework'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ManagementTab;
